import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import NoInternetIcon from '@assets/icons/no-internet.svg';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { AccountListFetchMoreError } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-table/account-list-fetch-more-error';
import { AccountListFilters } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-filters';
import { AccountListMobileList } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-mobile-list';
import { AccountListRowData } from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import {
  AccountListSkeleton,
  AccountListSkeletonRows,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-skeleton';
import { AccountListTable } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-table/account-list-table';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { buildAccountListServerFilters } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/transformers/account-list-transformers';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { partnerSupplier } from '@/utils/environment';
import { Platform, View, ViewStyle } from 'react-native';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalAccountsStackParams } from '@/navigators/types';
import {
  SEARCH_DEBOUNCE_MS,
  SearchInput,
} from '@/experiences/sales-portal/components/search-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAccountListFilterCountsLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/loaders/use-account-list-filter-counts-loader';
import { useAccountListFilterSelection } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/hooks/use-account-list-filter-selection';
import { useAccountListTablePaginatedLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/loaders/use-account-list-table-paginated-loader';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useDebouncedCallback } from 'use-debounce';
import { useLayoutGutter } from '@usepepper/sprout-kit-ui';
import { useNavigation } from '@react-navigation/native';

const LOADING_SKELETON_ROW_COUNT = 5;
const FETCHING_MORE_SKELETON_ROW_COUNT = 3;

export const AccountListContent: React.FC = () => {
  const [styles, theme] = useStyles();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalAccountsStackParams>>();
  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const debouncedSetSearchText = useDebouncedCallback((text: string) => {
    setDebouncedSearchText(text);
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedSetSearchText(text);
    },
    [debouncedSetSearchText],
  );

  const filterControls = useAccountListFilterSelection();
  const { selectedGroups, selectedStatuses } = filterControls;

  const serverFilters = useMemo(
    () =>
      buildAccountListServerFilters({
        searchText: debouncedSearchText,
        selectedGroups,
        selectedStatuses,
      }),
    [debouncedSearchText, selectedGroups, selectedStatuses],
  );

  const {
    accounts,
    canFetchMore,
    error,
    fetchingMore,
    fetchMore,
    fetchMoreError,
    loading,
    refetch,
  } = useAccountListTablePaginatedLoader({
    businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    filters: serverFilters,
  });

  const { groupCounts, restaurantGroups, statusCounts } =
    useAccountListFilterCountsLoader({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      filters: serverFilters,
    });

  const handleRowClick = useCallback(
    (row: AccountListRowData) => {
      navigation.push(SALES_PORTAL_ROUTES.AccountDetail, {
        restaurantUUID: row.restaurantUUID,
        supplierUUID: row.supplierUUID,
      });
    },
    [navigation],
  );

  const horizontalGutters = { paddingHorizontal: useLayoutGutter() };
  const handleBottomNavScroll = useBottomNavScrollHandler();

  const searchAndFilters = (
    <>
      <SearchInput
        onSearchChange={handleSearchChange}
        placeholderText={i18n.t('sales_portal.account_list.search_placeholder')}
        searchText={searchText}
      />
      <AccountListFilters
        {...filterControls}
        groupCounts={groupCounts}
        restaurantGroups={restaurantGroups}
        statusCounts={statusCounts}
      />
    </>
  );

  const tableLoadingState = (
    <AccountListSkeletonRows
      count={LOADING_SKELETON_ROW_COUNT}
      style={styles.tableLoadingContainer}
    />
  );

  const mobileTopInset = isMobileBottomNavLayout
    ? styles.mobileStateInset
    : undefined;

  const tableErrorState = (
    <ErrorState
      buttonLabel={i18n.t('sales_portal.error.refresh')}
      containerStyle={mobileTopInset}
      description={i18n.t('sales_portal.error.description')}
      icon={<NoInternetIcon stroke={theme.sk.color.icon.primary} />}
      onPress={() => refetch()}
      title={i18n.t('sales_portal.error.title')}
    />
  );

  const tableEmptyState = (
    <EmptyState
      containerStyle={mobileTopInset}
      description={i18n.t(
        debouncedSearchText
          ? 'sales_portal.account_list.search_empty.description'
          : 'sales_portal.account_list.filter.no_results.description',
      )}
      iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
      title={i18n.t(
        debouncedSearchText
          ? 'sales_portal.account_list.search_empty.title'
          : 'sales_portal.account_list.filter.no_results',
        debouncedSearchText ? { searchTerm: debouncedSearchText } : undefined,
      )}
    />
  );

  const listEmptyState = error
    ? tableErrorState
    : loading
      ? tableLoadingState
      : tableEmptyState;

  let listFooter: ReactElement | undefined;
  if (fetchingMore) {
    listFooter = (
      <AccountListSkeletonRows count={FETCHING_MORE_SKELETON_ROW_COUNT} />
    );
  } else if (fetchMoreError && canFetchMore) {
    listFooter = <AccountListFetchMoreError onRetry={fetchMore} />;
  }

  let bodyContent: React.ReactNode;
  if (loading && accounts.length === 0 && !serverFilters) {
    bodyContent = (
      <View style={[styles.content, horizontalGutters]}>
        <AccountListSkeleton />
      </View>
    );
  } else if (error && accounts.length === 0 && !serverFilters) {
    const errorState = (
      <ErrorState
        buttonLabel={i18n.t('sales_portal.error.refresh')}
        containerStyle={mobileTopInset}
        description={i18n.t('sales_portal.error.description')}
        icon={<NoInternetIcon stroke={theme.sk.color.icon.primary} />}
        onPress={() => refetch()}
        title={i18n.t('sales_portal.error.title')}
      />
    );
    bodyContent = (
      <View style={[styles.content, horizontalGutters]}>
        {isMobileBottomNavLayout ? (
          errorState
        ) : (
          <View style={styles.errorContainer}>{errorState}</View>
        )}
      </View>
    );
  } else if (accounts.length === 0 && !serverFilters) {
    bodyContent = (
      <View style={[styles.content, horizontalGutters]}>
        <EmptyState
          containerStyle={mobileTopInset}
          description={i18n.t('sales_portal.account_list.empty.description')}
          iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
          title={i18n.t('sales_portal.account_list.empty')}
        />
      </View>
    );
  } else {
    bodyContent = (
      <View style={styles.content}>
        {isMobileBottomNavLayout ? null : (
          <View style={[styles.chromeBlock, horizontalGutters]}>
            {searchAndFilters}
          </View>
        )}
        {isMobileBottomNavLayout ? (
          <AccountListMobileList
            data={accounts}
            emptyComponent={listEmptyState}
            listFooter={listFooter}
            onEndReached={canFetchMore ? fetchMore : undefined}
            onRowClick={handleRowClick}
          />
        ) : (
          <View style={[styles.tableBlock, horizontalGutters]}>
            <AccountListTable
              data={accounts}
              emptyComponent={listEmptyState}
              listFooter={listFooter}
              onEndReached={canFetchMore ? fetchMore : undefined}
              onRowClick={handleRowClick}
            />
          </View>
        )}
      </View>
    );
  }

  return (
    <AppPageLayout
      bodyGutter={Platform.OS === 'web' ? undefined : '$0'}
      mode="focused"
      onBodyScroll={isMobileBottomNavLayout ? handleBottomNavScroll : undefined}
      scroll={false}
      subBar={
        isMobileBottomNavLayout ? (
          <View style={styles.subBarBlock}>{searchAndFilters}</View>
        ) : undefined
      }
      title={i18n.t('sales_portal.account.title')}
    >
      {bodyContent}
    </AppPageLayout>
  );
};

const useStyles = createStyles((theme) => ({
  chromeBlock: {
    gap: theme.sk.spacing.spaceSMedium,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  content: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
  },
  errorContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    pointerEvents: 'box-none',
    ...Platform.select({
      web: {
        bottom: 0,
        left: 0,
        position: 'fixed' as ViewStyle['position'],
        right: 0,
        top: 0,
      },
    }),
  },
  mobileStateInset: {
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  subBarBlock: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  tableBlock: {
    flex: 1,
  },
  tableLoadingContainer: {
    height: '100%',
    width: '100%',
  },
}));
